let audioCtx = null;
let unlocked = false;

const LS_KEY = 'iugb_notification_sound_prefs_v1';

function loadPrefs() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return {
      muted: Boolean(parsed?.muted),
      volume: typeof parsed?.volume === 'number' ? parsed.volume : 0.35,
    };
  } catch {
    return { muted: false, volume: 0.35 };
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function getNotificationSoundPrefs() {
  return loadPrefs();
}

export function setNotificationSoundPrefs(next) {
  const current = loadPrefs();
  const merged = { ...current, ...(next || {}) };
  const volume = Number(merged.volume);
  merged.volume = Number.isFinite(volume) ? Math.max(0, Math.min(1, volume)) : 0.35;
  merged.muted = Boolean(merged.muted);
  savePrefs(merged);
  return merged;
}

function ensureAudioContext() {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  return audioCtx;
}

async function ensureResumed() {
  const ctx = ensureAudioContext();
  if (!ctx) return null;
  try {
    if (ctx.state === 'suspended') await ctx.resume();
    return ctx;
  } catch {
    return ctx;
  }
}

export function getNotificationSoundDebugInfo() {
  const prefs = loadPrefs();
  const ctx = audioCtx;
  return {
    supported: Boolean(window.AudioContext || window.webkitAudioContext),
    unlocked,
    prefs,
    audioContextState: ctx ? ctx.state : 'none',
  };
}

export function initNotificationSounds() {
  if (unlocked) return;

  const unlock = async () => {
    const ctx = await ensureResumed();
    if (!ctx) return;

    try {
      // do a tiny silent beep to fully unlock on some browsers
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.01);
      unlocked = true;
    } catch {
      // ignore
    }
  };

  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
}

async function beepPattern({ tones, volume }) {
  const ctx = await ensureResumed();
  if (!ctx) return;

  const now = ctx.currentTime;
  let t = now;

  tones.forEach((tone) => {
    const { freq, durationMs, gapMs } = tone;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(volume, t + 0.01);
    gain.gain.linearRampToValueAtTime(0, t + durationMs / 1000);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(t);
    osc.stop(t + durationMs / 1000 + 0.02);

    t += durationMs / 1000 + (gapMs || 0) / 1000;
  });
}

export async function playUrgentSound() {
  const { muted, volume } = loadPrefs();
  if (muted) return;
  initNotificationSounds();

  // 3 bips rapides, médium/grave
  await beepPattern({
    volume,
    tones: [
      { freq: 520, durationMs: 90, gapMs: 70 },
      { freq: 520, durationMs: 90, gapMs: 70 },
      { freq: 440, durationMs: 110, gapMs: 0 },
    ],
  });
}

export async function playWarningSound() {
  const { muted, volume } = loadPrefs();
  if (muted) return;
  initNotificationSounds();

  // 2 bips doux, plus haut
  await beepPattern({
    volume: Math.min(1, volume * 0.9),
    tones: [
      { freq: 660, durationMs: 80, gapMs: 90 },
      { freq: 660, durationMs: 100, gapMs: 0 },
    ],
  });
}

export async function playTestSound() {
  await playUrgentSound();
}
